import React, { useState, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Card,
  CardContent,
  TextField,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { School, Add, Edit, Delete, Block, CheckCircle } from '@mui/icons-material';
import TableComponent from 'Component/Table';
import useApiCaller from 'hooks/apiCaller';
import { useNavigate } from 'react-router-dom';

const EditCourses = () => {
  const [tabIndex, setTabIndex] = useState(0);

  // Active courses state
  const [allCourses, setAllCourses] = useState([]);
  const [count, setCount] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  // Disabled courses state
  const [disabledCourses, setDisabledCourses] = useState([]);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [courseToDisable, setCourseToDisable] = useState(null);
  const [disableReason, setDisableReason] = useState('');
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [courseToEnable, setCourseToEnable] = useState(null);

  const navigate = useNavigate();
  const apiCaller = useApiCaller();

  const activeColumns = [
    { label: 'Course Title', field: 'courseTitle' },
    { label: 'Assigned', field: 'isAssigned' },
    { label: 'Active', field: 'isActive' },
    { label: 'Mandatory', field: 'isMandatory' },
    { label: 'Course Type', field: 'courseType' },
    {
      label: 'Edit',
      field: 'edit',
      render: (row) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(`/update-course/${row._id}`, { state: { courseId: row._id } })}
        >
          Edit
        </Button>
      ),
    },
    {
      label: 'Disable',
      field: 'disable',
      render: (row) => (
        <Button
          variant="contained"
          color="warning"
          startIcon={<Block />}
          onClick={() => {
            setCourseToDisable(row);
            setDisableReason('');
            setDisableDialogOpen(true);
          }}
        >
          Disable
        </Button>
      ),
    },
    {
      label: 'Delete',
      field: 'delete',
      render: (row) => (
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setCourseToDelete(row);
            setDeleteDialogOpen(true);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const disabledColumns = [
    { label: 'Course Title', field: 'courseTitle' },
    {
      label: 'Disabled By (ID)',
      field: 'disabledBy',
      render: (row) => <Typography variant="body2">{row.disabledBy}</Typography>,
    },
    {
      label: 'Disabled At',
      field: 'disabledAt',
      render: (row) => (
        <Typography variant="body2">
          {row.disabledAt ? new Date(row.disabledAt).toLocaleString() : '—'}
        </Typography>
      ),
    },
    {
      label: 'Reason',
      field: 'reason',
      render: (row) => (
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {row.reason || '—'}
        </Typography>
      ),
    },
    {
      label: 'Enable',
      field: 'enable',
      render: (row) => (
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          onClick={() => {
            setCourseToEnable(row);
            setEnableDialogOpen(true);
          }}
        >
          Enable
        </Button>
      ),
    },
  ];

  const handleShowCourses = async () => {
    try {
      const response = await apiCaller.get('/super-user/getAllCourse');
      if (response) {
        setAllCourses(response?.data?.data?.courses);
        setCount(response?.data?.data?.count);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleShowDisabledCourses = async () => {
    try {
      const response = await apiCaller.get('/super-user/getDisabledCourses');
      if (response) {
        setDisabledCourses(response?.data?.data?.disabledCourses || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteCourse = async () => {
    try {
      const response = await apiCaller.post(`/super-user/deleteCourse`, {
        courseId: courseToDelete._id,
      });
      if (response) {
        handleShowCourses();
        setDeleteDialogOpen(false);
        setCourseToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting course:', error);
    }
  };

  const handleDisableCourse = async () => {
    try {
      const response = await apiCaller.post('/super-user/disableCourse', {
        courseId: courseToDisable._id,
        reason: disableReason,
      });
      if (response) {
        handleShowCourses();
        handleShowDisabledCourses();
        setDisableDialogOpen(false);
        setCourseToDisable(null);
        setDisableReason('');
      }
    } catch (error) {
      console.error('Error disabling course:', error);
    }
  };

  const handleEnableCourse = async () => {
    try {
      const response = await apiCaller.post('/super-user/enableCourse', {
        courseId: courseToEnable.courseId,
      });
      if (response) {
        handleShowDisabledCourses();
        setEnableDialogOpen(false);
        setCourseToEnable(null);
      }
    } catch (error) {
      console.error('Error enabling course:', error);
    }
  };

  useEffect(() => {
    handleShowCourses();
    handleShowDisabledCourses();
  }, []);

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography variant="h5" fontWeight="600" sx={{ color: '#1e293b', mb: 0.25 }}>
                Course Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Edit, delete, disable, and manage all courses in your system
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<Block />}
                onClick={() => setTabIndex(1)}
                sx={{
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  fontWeight: '600',
                  textTransform: 'none',
                  borderColor: '#f59e0b',
                  color: '#f59e0b',
                  '&:hover': {
                    borderColor: '#d97706',
                    color: '#d97706',
                    backgroundColor: 'rgba(245, 158, 11, 0.04)',
                  },
                }}
              >
                Disabled Courses
                {disabledCourses.length > 0 && (
                  <Chip
                    label={disabledCourses.length}
                    size="small"
                    sx={{ ml: 1, background: '#f59e0b', color: '#fff', height: 20, fontSize: 11 }}
                  />
                )}
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/upload-course')}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  fontWeight: '600',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px 0 rgba(59, 130, 246, 0.4)',
                  },
                }}
              >
                Create New Course
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            sx={{
              background: '#fff',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              px: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            }}
          >
            <Tab label="Active Courses" />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  Disabled Courses
                  {disabledCourses.length > 0 && (
                    <Chip
                      label={disabledCourses.length}
                      size="small"
                      sx={{ background: '#f59e0b', color: '#fff', height: 18, fontSize: 10 }}
                    />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Active Courses Table */}
        {tabIndex === 0 && (
          <Card
            sx={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <TableComponent
                tableData={allCourses}
                setTableData={setAllCourses}
                columns={activeColumns}
                tableName="Course"
                count={count}
                useWholeRowClicked={false}
              />
            </CardContent>
          </Card>
        )}

        {/* Disabled Courses Table */}
        {tabIndex === 1 && (
          <Card
            sx={{
              background: '#ffffff',
              border: '1px solid #fef3c7',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {disabledCourses.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ color: '#64748b' }}>
                    No disabled courses.
                  </Typography>
                </Box>
              ) : (
                <TableComponent
                  tableData={disabledCourses}
                  setTableData={setDisabledCourses}
                  columns={disabledColumns}
                  tableName="DisabledCourse"
                  count={disabledCourses.length}
                  useWholeRowClicked={false}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
            },
          }}
        >
          <DialogTitle
            sx={{ fontWeight: '600', color: '#1e293b', borderBottom: '1px solid #e2e8f0', pb: 2 }}
          >
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ color: '#64748b' }}>
              Are you sure you want to delete the course "{courseToDelete?.courseTitle}"? This
              action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1,
                fontWeight: '600',
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  backgroundColor: 'rgba(59, 130, 246, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCourse}
              color="error"
              variant="contained"
              startIcon={<Delete />}
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1,
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 4px 14px 0 rgba(239, 68, 68, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px 0 rgba(239, 68, 68, 0.4)',
                },
              }}
            >
              Delete Course
            </Button>
          </DialogActions>
        </Dialog>

        {/* Disable Confirmation Dialog */}
        <Dialog
          open={disableDialogOpen}
          onClose={() => setDisableDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)',
              border: '1px solid #fde68a',
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={{ fontWeight: '600', color: '#1e293b', borderBottom: '1px solid #fde68a', pb: 2 }}
          >
            Disable Course
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ color: '#64748b', mb: 2 }}>
              Disabling "{courseToDisable?.courseTitle}" will hide it from all users. You can
              re-enable it later from the Disabled Courses tab.
            </Typography>
            <TextField
              label="Reason (optional)"
              fullWidth
              multiline
              rows={2}
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              placeholder="e.g. Content under review"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setDisableDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1,
                fontWeight: '600',
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#64748b',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisableCourse}
              variant="contained"
              startIcon={<Block />}
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1,
                fontWeight: '600',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 4px 14px 0 rgba(245, 158, 11, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px 0 rgba(245, 158, 11, 0.4)',
                },
              }}
            >
              Disable Course
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enable Confirmation Dialog */}
        <Dialog
          open={enableDialogOpen}
          onClose={() => setEnableDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
              border: '1px solid #bbf7d0',
            },
          }}
        >
          <DialogTitle
            sx={{ fontWeight: '600', color: '#1e293b', borderBottom: '1px solid #bbf7d0', pb: 2 }}
          >
            Enable Course
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ color: '#64748b' }}>
              Are you sure you want to re-enable "{courseToEnable?.courseTitle}"? It will become
              visible to users again.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setEnableDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1,
                fontWeight: '600',
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#64748b',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnableCourse}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1,
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 4px 14px 0 rgba(34, 197, 94, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px 0 rgba(34, 197, 94, 0.4)',
                },
              }}
            >
              Enable Course
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default EditCourses;
